import { clsx } from 'clsx';
import { forwardRef } from 'react';
import {
  Button as ReactAriaButton,
  ButtonProps as ReactAriaButtonProps,
  ButtonRenderProps as ReactAriaButtonRenderProps,
} from 'react-aria-components';

import styles from './ButtonBase.module.scss';

export const buttonBaseSizes = ['compact', 'small', 'medium', 'large'] as const;

export const buttonBaseVariants = [
  'brand',
  'danger',
  'ghost',
  'primary',
  'secondary',
  'tertiary',
] as const;

export type ButtonBaseProps = ReactAriaButtonProps & {
  /** Adjusts colors for use on dark/inverted backgrounds. */
  inverted?: boolean;
  /** The size, impacting its dimensions and typography. */
  size?: (typeof buttonBaseSizes)[number];
  /** The color style to render. */
  variant?: (typeof buttonBaseVariants)[number];
};

export type ButtonBaseRenderProps = ReactAriaButtonRenderProps;

/**
 * Base button component with PDL styles and no children.
 *
 * Prefer `Button` unless you need advanced customization.
 */
export const ButtonBase = forwardRef<HTMLButtonElement, ButtonBaseProps>(
  (
    {
      children,
      className,
      isDisabled = false,
      isPending = false,
      inverted = false,
      size = 'medium',
      variant = 'primary',
      ...props
    },
    ref
  ) => {
    return (
      <ReactAriaButton
        ref={ref}
        className={(renderProps) => {
          const dynamicClassName =
            typeof className === 'function'
              ? className(renderProps)
              : className;
          return clsx(dynamicClassName, styles.buttonBase);
        }}
        data-a11y-ignore={variant === 'brand' ? 'color-contrast' : undefined}
        data-inverted={inverted || undefined}
        data-zds
        data-size={size}
        data-variant={variant}
        isDisabled={isDisabled || isPending}
        isPending={isPending}
        {...props}
      >
        {(buttonRenderProps) =>
          typeof children === 'function'
            ? children(buttonRenderProps)
            : children
        }
      </ReactAriaButton>
    );
  }
);
